import React, { useEffect, useState } from "react";
import Dialog from "../../../components/Dialog/Dialog";
import DialogTextField from "../../../components/DialogTextField/DialogTextField";
import EditableCompactList from "../../../components/EditableCompactList/EditableCompactList";
import { ParentRole } from "../Models/ParentRole";
import { SubRoleDetailedOverview } from "../../SubRolesPage/Models/SubRoleDetailedOverview";
import { Privilege } from "../../PrivilegesPage/Models/Privilige";
import {
  getRequest,
  PRIVILEGES_OVERVIEW_ENDPOINT,
  SUB_ROLES_DETAILED_OVERVIEW_ENDPOINT,
  PARENT_ROLES_ENDPOINT,
} from "../../../ApiService";
import {
  DESCRIPTION,
  ERROR_MESSAGE,
  ERROR_TITLE,
  NAME,
  OTHER_PRIVILEGES,
  SUB_ROLES,
} from "../../../utility/TextLabels";
import { ParentRoleDetails } from "../Models/ParentRoleDetails";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";

type ParentRoleDialogProps = {
  title: string;
  existingId?: string;
  existingName?: string;
  existingDescription?: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (parentRole: ParentRole) => void;
};

const ParentRoleDialog: React.FC<ParentRoleDialogProps> = ({
  title,
  existingId,
  existingName,
  existingDescription,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitValid, setIsSubmitValid] = useState(false);
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false);

  const [selectableSubRoles, setSelectableSubRoles] = useState<
    SubRoleDetailedOverview[]
  >([]);
  const [selectablePrivileges, setSelectablePrivileges] = useState<Privilege[]>(
    []
  );

  const [selectedSubRoleIds, setSelectedSubRoleIds] = useState<string[]>([]);
  const [selectedPrivilegeIds, setSelectedPrivilegeIds] = useState<string[]>(
    []
  );

  const [preSelectedSubRoles, setPreSelectedSubRoles] = useState<string[]>([]);
  const [preSelectedPrivileges, setPreSelectedPrivileges] = useState<string[]>(
    []
  );

  useEffect(() => {
    const isNameFieldFilled = name.length > 1;
    setIsSubmitValid(isNameFieldFilled);
  }, [name]);

  useEffect(() => {
    if (!isOpen) return;
    getSubRolesAndPrivileges();
    if (existingId) {
      getParentRoleDetails(existingId);
    }
  }, [isOpen, existingId]);

  const getParentRoleDetails = async (id: string) => {
    try {
      await getRequest(PARENT_ROLES_ENDPOINT, id).then(
        (parentRoleDetails: ParentRoleDetails) => {
          setName(parentRoleDetails.name);
          setDescription(parentRoleDetails.description ?? "");

          const subRoleNames =
            parentRoleDetails.subRoleDetails?.map((subRole) => subRole.name) ||
            [];
          setPreSelectedSubRoles(subRoleNames);

          const subRoleIds =
            parentRoleDetails.subRoleDetails?.map((subRole) => subRole?.id) ||
            [];
          setSelectedSubRoleIds(subRoleIds);

          const privilegeNames =
            parentRoleDetails.privilegeDetails?.map(
              (privilege) => privilege.name
            ) || [];
          setPreSelectedPrivileges(privilegeNames);

          const privilegeIds =
            parentRoleDetails.privilegeDetails?.map(
              (privilege) => privilege?.id
            ) || [];
          setSelectedPrivilegeIds(privilegeIds);
        }
      );
    } catch (error) {
      setIsErrorMessageOpen(true);
    }
  };

  const getSubRolesAndPrivileges = async () => {
    try {
      await Promise.all([
        getRequest(SUB_ROLES_DETAILED_OVERVIEW_ENDPOINT),
        getRequest(PRIVILEGES_OVERVIEW_ENDPOINT),
      ]).then(([subRoleResult, privilegeResult]) => {
        setSelectableSubRoles(subRoleResult);
        setSelectablePrivileges(privilegeResult);
      });
    } catch (error) {
      setIsErrorMessageOpen(true);
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDescription(event.target.value);
  };

  const addSubRoleId = (name: string) => {
    const subRoleToAdd: SubRoleDetailedOverview | undefined =
      selectableSubRoles.find((subRole) => subRole.name === name);
    if (subRoleToAdd) {
      setSelectedSubRoleIds([...selectedSubRoleIds, subRoleToAdd.id]);
    }
  };

  const addPrivilegeId = (name: string) => {
    const privilegeToAdd: Privilege | undefined = selectablePrivileges.find(
      (privilege) => privilege.name === name
    );
    if (privilegeToAdd) {
      setSelectedPrivilegeIds([...selectedPrivilegeIds, privilegeToAdd.id!]);
    }
  };

  const removeSubRoleId = (name: string) => {
    const subRoleToRemove: SubRoleDetailedOverview | undefined =
      selectableSubRoles.find((subRole) => subRole.name === name);

    if (subRoleToRemove) {
      const updatedSubRoles: string[] = selectedSubRoleIds.filter(
        (subRoleId) => subRoleId !== subRoleToRemove.id
      );
      setSelectedSubRoleIds(updatedSubRoles);
    }
  };

  const removePrivilegeId = (name: string) => {
    const privilegeToRemove: Privilege | undefined = selectablePrivileges.find(
      (privilege) => privilege.name === name
    );

    if (privilegeToRemove) {
      const updatedPrivileges: string[] = selectedPrivilegeIds.filter(
        (privilegeId) => privilegeId !== privilegeToRemove.id
      );
      setSelectedPrivilegeIds(updatedPrivileges);
    }
  };

  const prepareSubmit = () => {
    const parentRole: ParentRole = {
      name: name,
      description: description,
      subRoleIds: selectedSubRoleIds,
      privilegeIds: selectedPrivilegeIds,
    };

    onSubmit(parentRole);
    prepareClose();
  };

  const prepareClose = () => {
    setName("");
    setDescription("");
    setSelectableSubRoles([]);
    setSelectablePrivileges([]);
    setSelectedSubRoleIds([]);
    setSelectedPrivilegeIds([]);
    onClose();
  };

  return (
    <>
      <ErrorMessage
        isOpen={isErrorMessageOpen}
        primaryText={ERROR_TITLE}
        additionalText={ERROR_MESSAGE}
        onClose={() => setIsErrorMessageOpen(false)}
      ></ErrorMessage>

      <Dialog
        title={title}
        open={isOpen}
        onClose={prepareClose}
        onSubmit={prepareSubmit}
        isSubmitEnabled={isSubmitValid}
      >
        <DialogTextField
          label={NAME}
          name={existingName}
          inputValue={name}
          onChange={handleNameChange}
        />
        <DialogTextField
          label={DESCRIPTION}
          name={existingDescription}
          rows={3}
          inputValue={description}
          onChange={handleDescriptionChange}
        />
        <EditableCompactList
          label={SUB_ROLES}
          preSelectedItems={preSelectedSubRoles}
          selectableItems={selectableSubRoles}
          onAddingItem={addSubRoleId}
          onRemovingItem={removeSubRoleId}
        />
        <EditableCompactList
          label={OTHER_PRIVILEGES}
          preSelectedItems={preSelectedPrivileges}
          selectableItems={selectablePrivileges}
          onAddingItem={addPrivilegeId}
          onRemovingItem={removePrivilegeId}
        />
      </Dialog>
    </>
  );
};

export default ParentRoleDialog;
